import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ScrollText } from 'lucide-react';
import { Button, DataTable, DateField, EmptyState, ErrorState, FormField, Modal, PageHeader, SearchPanel, Select, StatusBadge, TextField, type Column } from '@/components';
import { formatDateTime } from '@/lib/format';
import { useAuditLogs, type AuditFilter, type AuditLogDto } from './api';
import './admin.css';

const ACTIONS: [string, string][] = [
  ['login', 'ログイン'],
  ['login_failed', 'ログイン失敗'],
  ['logout', 'ログアウト'],
  ['create', '登録'],
  ['update', '更新'],
  ['delete', '削除'],
  ['export', '帳票出力'],
  ['import', '取込'],
  ['approve', '承認'],
  ['reject', '否認'],
  ['bulk_approval', '一括承認'],
  ['annual_update', '年次更新'],
  // セキュリティ機能
  ['login_mfa_pending', '2段階認証待ち'],
  ['mfa_enable', '2段階認証 有効化'],
  ['mfa_disable', '2段階認証 無効化'],
  ['mfa_reset', '2段階認証 リセット'],
  ['mfa_failed', '2段階認証 失敗'],
  ['mfa_recovery_used', 'リカバリーコード使用'],
  ['mfa_recovery_regenerate', 'リカバリーコード再発行'],
  ['ip_denied', 'IP 制限で拒否'],
  ['device_cert_denied', '端末証明書で拒否'],
  ['revoke', '失効'],
];
const ACTION_LABEL = new Map(ACTIONS);
const PAGE_SIZE = 50;

function actionTone(a: string): 'success' | 'danger' | 'info' | 'warning' | 'neutral' | 'navy' {
  if (a === 'delete' || a === 'login_failed' || a === 'reject' || a === 'mfa_failed' || a === 'ip_denied' || a === 'device_cert_denied' || a === 'revoke') return 'danger';
  if (a === 'mfa_reset' || a === 'mfa_disable' || a === 'mfa_recovery_used') return 'warning';
  if (a === 'create') return 'success';
  if (a === 'update' || a === 'bulk_approval' || a === 'annual_update') return 'info';
  if (a === 'export') return 'navy';
  return 'neutral';
}

/** S-95 操作ログ（基金のみ）。期間・ユーザー・操作種別で検索 */
export function AuditLogsPage() {
  const [params, setParams] = useSearchParams();
  const filter = useMemo<AuditFilter>(
    () => ({ from: params.get('from') ?? '', to: params.get('to') ?? '', loginId: params.get('loginId') ?? '', action: params.get('action') ?? '', targetType: params.get('targetType') ?? '' }),
    [params],
  );
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);
  const { data, isPending, isFetching, error, refetch } = useAuditLogs(filter, page, PAGE_SIZE);
  const { register, handleSubmit, reset } = useForm<AuditFilter>({ values: filter });
  const [detail, setDetail] = useState<AuditLogDto | null>(null);

  const apply = (v: AuditFilter, p = 1) => {
    const next = new URLSearchParams();
    Object.entries(v).forEach(([k, val]) => val && next.set(k, String(val)));
    if (p > 1) next.set('page', String(p));
    setParams(next, { replace: true });
  };

  const columns: Column<AuditLogDto>[] = [
    { key: 'createdAt', header: '日時', width: 170, render: (l) => <span className="bs-num">{formatDateTime(l.createdAt)}</span> },
    { key: 'loginId', header: 'ユーザー', width: 150, render: (l) => l.loginId ?? '—' },
    { key: 'action', header: '操作', width: 130, render: (l) => <StatusBadge tone={actionTone(l.action)}>{ACTION_LABEL.get(l.action) ?? l.action}</StatusBadge> },
    { key: 'targetType', header: '対象', render: (l) => l.targetType ?? '—' },
    { key: 'targetId', header: '対象ID', width: 90, render: (l) => <span className="bs-num">{l.targetId ?? '—'}</span> },
    { key: 'fiscalYear', header: '年度', numeric: true, width: 80, render: (l) => l.fiscalYear ?? '—' },
    { key: 'ipAddress', header: 'IPアドレス', width: 140, render: (l) => <span className="bs-num">{l.ipAddress ?? '—'}</span> },
    { key: 'detail', header: '詳細', width: 80, align: 'center', render: (l) => (l.detail != null ? <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setDetail(l); }}>表示</Button> : null) },
  ];

  const summary = [filter.from && `${filter.from}〜`, filter.to && `〜${filter.to}`, filter.loginId && `ユーザー: ${filter.loginId}`, filter.action && (ACTION_LABEL.get(filter.action) ?? filter.action), filter.targetType && `対象: ${filter.targetType}`].filter(Boolean).join(' ／ ');

  return (
    <>
      <PageHeader eyebrow="システム管理" title="操作ログ" description="ログイン・登録・更新・削除・帳票出力などの操作履歴を確認します。" />
      <SearchPanel
        onSubmit={handleSubmit((v) => apply(v))}
        onReset={() => {
          reset({ from: '', to: '', loginId: '', action: '', targetType: '' });
          apply({});
        }}
        summary={summary || '条件なし'}
        searching={isFetching}
        columns={5}
      >
        <FormField label="期間 FROM">
          <DateField {...register('from')} />
        </FormField>
        <FormField label="期間 TO">
          <DateField {...register('to')} />
        </FormField>
        <FormField label="ユーザー（ログインID）" hint="部分一致">
          <TextField {...register('loginId')} />
        </FormField>
        <FormField label="操作種別">
          <Select placeholder="すべて" options={ACTIONS.map(([value, label]) => ({ value, label }))} {...register('action')} />
        </FormField>
        <FormField label="対象種別" hint="例：vessels、announcements">
          <TextField {...register('targetType')} />
        </FormField>
      </SearchPanel>

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <DataTable
          caption="操作ログ"
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(l) => l.id}
          loading={isPending}
          dense
          pagination={{ pageSize: PAGE_SIZE, page, total: data?.total ?? 0, onPageChange: (p) => apply(filter, p) }}
          onRowClick={(l) => l.detail != null && setDetail(l)}
          empty={<EmptyState compact icon={<ScrollText />} title="該当する操作ログはありません" description="条件を変更してください。" />}
        />
      )}
      <Modal open={detail != null} onClose={() => setDetail(null)} size="lg" title={detail ? `${ACTION_LABEL.get(detail.action) ?? detail.action}：${detail.targetType ?? ''} ${detail.targetId ?? ''}` : ''} footer={<Button onClick={() => setDetail(null)}>閉じる</Button>}>
        <pre className="bs-audit-detail">{detail ? JSON.stringify(detail.detail, null, 2) : ''}</pre>
      </Modal>
    </>
  );
}
